import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Globe, BookOpen, Target } from 'lucide-react';
import { getSourceTypeOptions } from '@/config/source-config';
import { StandardSelect, SelectOption } from '@/components/ui/form-fields/StandardSelect';
import { getCountryOptions } from '@/lib/countries';
import { getResearchTypeById, RESEARCH_TYPES } from '@/types/research-types';
import { SimpleAdvancedToggle } from './SimpleAdvancedToggle';

interface ResearchParameterControlsProps {
  timeframe: string;
  country: string;
  sourceType: string;
  selectedType?: string;
  filters?: Record<string, string>;
  isSimpleMode: boolean;
  onTimeframeChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onSourceTypeChange: (value: string) => void;
  onFiltersChange?: (filters: Record<string, string>) => void;
  onSimpleModeToggle: (isSimple: boolean) => void;
  onTypeChange: (type: string | null) => void;
}

const ResearchParameterControls: React.FC<ResearchParameterControlsProps> = React.memo(({
  timeframe,
  country,
  sourceType,
  selectedType,
  filters = {},
  isSimpleMode,
  onTimeframeChange,
  onCountryChange,
  onSourceTypeChange,
  onFiltersChange,
  onSimpleModeToggle,
  onTypeChange
}) => {
  const countryOptions = getCountryOptions();
  const sourceTypeOptions = getSourceTypeOptions();

  // Create research type dropdown options
  const researchTypeOptions: SelectOption[] = [
    { value: 'none', label: 'None (General Research)' },
    ...RESEARCH_TYPES.map(type => ({
      value: type.id,
      label: type.name
    }))
  ];


  const handleResearchTypeChange = (value: string | string[]) => {
    const finalValue = Array.isArray(value) ? value[0] || '' : value;
    onTypeChange(finalValue === 'none' ? null : finalValue);
  };


  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {/* Simple/Advanced Toggle - replaces "Research Parameters" header */}
        <SimpleAdvancedToggle 
          isSimple={isSimpleMode}
          onToggle={onSimpleModeToggle}
        />

        {/* Research Type Dropdown - NEW: Added to Simple section */}
        <StandardSelect
          id="research-type"
          label="Research Type"
          icon={<Target size={16} />}
          colorVariant="purple"
          value={selectedType || ''}
          onChange={handleResearchTypeChange}
          options={researchTypeOptions}
          placeholder="Research Type"
        />

{/* Type-Specific Filters removed - no longer showing dynamic filters based on research type */}

        {/* Simple Mode Parameters - Only Country visible in Simple mode */}
        {/* Country */}
        <StandardSelect
          id="country"
          label="Country"
          icon={<Globe size={16} />}
          colorVariant="blue"
          value={country === 'none' ? '' : country}
          onChange={onCountryChange}
          options={countryOptions}
          placeholder="Country"
        />

        {/* Advanced mode controls - Timeframe and Source Type */}
        <AnimatePresence>
          {!isSimpleMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {/* Timeframe - moved to Advanced mode */}
              <StandardSelect
                id="timeframe"
                label="Timeframe"
                icon={<Clock size={16} />}
                colorVariant="blue"
                value={timeframe === 'none' ? '' : timeframe}
                onChange={onTimeframeChange}
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'last_30_days', label: 'Last 30 Days' },
                  { value: 'last_3_months', label: 'Last 3 Months' },
                  { value: 'last_12_months', label: 'Last 12 Months' },
                  { value: 'this_year', label: 'This Year' },
                  { value: 'last_3_years', label: 'Last 3 Years' },
                  { value: 'all', label: 'All Time' }
                ]}
                placeholder="Timeframe"
              />

              {/* Source Type */}
              <StandardSelect
                id="sourceType"
                label="Source Type"
                icon={<BookOpen size={16} />}
                colorVariant="blue"
                value={sourceType === 'none' ? '' : sourceType}
                onChange={onSourceTypeChange}
                options={sourceTypeOptions}
                placeholder="Source Type"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

ResearchParameterControls.displayName = 'ResearchParameterControls';

export default ResearchParameterControls;
